import { FormEvent, useEffect, useMemo, useState } from 'react'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { Theme, alpha } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

import * as EmailValidator from 'email-validator'

import config from 'src/config'
import { resolveContactModalPalette } from './palette'
import shopInfos from 'template/infos'
import GoogleRecaptchaInfos from 'src/components/GoogleRecaptchaInfos'
import { pageBuilderButtonSx, pageBuilderOutlinedButtonSx } from 'src/theme/pageBuilderButtons'
import { post } from 'src/functions/fetch'
import getRecaptchaToken from 'src/functions/getRecaptchaToken'
import { isValidTelefon } from 'src/functions/validators'
import { MenuPageBuilderContactSubject } from 'src/shared/dbTypes/menuPageBuilder'
import dsgvoStyle from 'src/theme/dsgvoStyle'

const subjectOptions: { value: MenuPageBuilderContactSubject; label: string }[] = [
  { value: 'rueckruf', label: 'Rückruf' },
  { value: 'termin', label: 'Terminvereinbarung' },
  { value: 'angebot', label: 'Angebotsanfrage' },
  { value: 'sonstiges', label: 'Sonstiges' },
]

const modalPalette = resolveContactModalPalette(config.contactModalPalette)
const { ink: modalInk, border: modalBorder, white: modalWhite, accent: modalAccent, shadow: modalShadow } = modalPalette

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // Dialogs are portalled outside Layout and do not inherit its Builder palette.
    palette: {
      '& .MuiDialog-paper, & .MuiInputBase-root, & .MuiInputLabel-root.Mui-focused:not(.Mui-error)': {
        color: 'var(--page-builder-text)',
      },
      '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--page-builder-text)' },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--page-builder-text)' },
    },
    paper: {
      borderRadius: 28,
      overflow: 'hidden',
      background: 'var(--page-builder-background, #f7f2eb)',
      boxShadow: `0 34px 90px ${alpha(modalPalette.dialogShadow, .42)}`,
      [theme.breakpoints.down('sm')]: {
        borderRadius: 22,
        margin: theme.spacing(1.5),
      },
    },
    titleBar: {
      position: 'relative',
      display: 'grid',
      gap: theme.spacing(.5),
      padding: theme.spacing(3.25, 4, 3),
      color: 'var(--page-builder-button-text, #fffaf4)',
      background: 'linear-gradient(135deg, var(--page-builder-button-bg, #7e634b), var(--page-builder-button-hover, #4e392a))',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.42), transparent)',
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2.75, 2.5, 2.5),
      },
    },
    eyebrow: {
      color: alpha(modalWhite, .72),
      fontSize: '.72rem',
      fontWeight: 700,
      letterSpacing: '.18em',
      textTransform: 'uppercase',
    },
    title: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 'clamp(2rem, 6vw, 3.1rem)',
      fontWeight: 500,
      letterSpacing: 0,
      lineHeight: 1.05,
    },
    titleSubline: {
      maxWidth: 510,
      color: alpha(modalWhite, .78),
      fontSize: '.96rem',
      lineHeight: 1.6,
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(1.5),
      right: theme.spacing(1.5),
      color: modalWhite,
      backgroundColor: alpha('#fff', .1),
      border: `1px solid ${alpha('#fff', .18)}`,
      '&:hover': {
        backgroundColor: alpha('#fff', .18),
      },
    },
    content: {
      padding: `${theme.spacing(3.25, 4, 2.5)} !important`,
      background: 'linear-gradient(180deg, var(--page-builder-surface, #fbf7f1) 0%, var(--page-builder-background, #f3ece3) 100%)',
      [theme.breakpoints.down('sm')]: {
        padding: `${theme.spacing(2.5, 2.25, 2)} !important`,
      },
    },
    intro: {
      display: 'grid',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(3),
    },
    directContact: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
    contactChip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(.75),
      minHeight: 36,
      padding: theme.spacing(.75, 1.25),
      borderRadius: 999,
      color: 'var(--page-builder-text, #2a211a)',
      backgroundColor: 'var(--page-builder-surface, rgba(255,255,255,.76))',
      border: `1px solid ${alpha(modalBorder, .12)}`,
      boxShadow: `0 10px 26px ${alpha(modalPalette.chipShadow, .06)}`,
      textDecoration: 'none',
      '& a': {
        color: 'inherit',
      },
    },
    formCard: {
      display: 'grid',
      gap: theme.spacing(2.1),
      padding: theme.spacing(2.25),
      borderRadius: 22,
      backgroundColor: 'var(--page-builder-surface, rgba(255,255,255,.72))',
      border: `1px solid ${alpha(modalBorder, .12)}`,
      boxShadow: `0 18px 44px ${alpha(modalInk, .08)}`,
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
        borderRadius: 18,
      },
    },
    form: {
      display: 'grid',
      gap: theme.spacing(2),
    },
    field: {
      '& .MuiInputLabel-root': {
        color: alpha(modalInk, modalPalette.mutedOpacity),
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 14,
        backgroundColor: alpha('#fff', .82),
        transition: 'box-shadow 180ms ease, background-color 180ms ease',
        '& fieldset': {
          borderColor: alpha(modalBorder, .18),
        },
        '&:hover fieldset': {
          borderColor: alpha(modalBorder, .34),
        },
        '&.Mui-focused': {
          backgroundColor: '#fff',
          boxShadow: `0 0 0 4px ${alpha(modalAccent, .13)}`,
        },
        '&.Mui-focused fieldset': {
          borderColor: 'var(--page-builder-button-bg, #7e634b)',
        },
      },
      '& .MuiInputAdornment-root': {
        color: alpha(modalInk, .55),
      },
    },
    grid: {
      display: 'grid',
      gap: theme.spacing(2),
      [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
    },
    dsgvo: {
      ...dsgvoStyle(theme, { switch: true }),
      alignItems: 'flex-start',
      borderRadius: 16,
      marginTop: theme.spacing(.25),
      padding: theme.spacing(1.5),
      backgroundColor: 'var(--page-builder-background, rgba(239,231,221,.72))',
      border: `1px solid ${alpha(modalBorder, .1)}`,
      boxShadow: 'none',
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: 'var(--page-builder-button-bg, #7e634b)',
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: 'var(--page-builder-button-bg, #7e634b)',
      },
    },
    dsgvoText: {
      color: 'var(--page-builder-text, #332820)',
      fontSize: '.9rem',
      lineHeight: 1.55,
      '& p': {
        margin: 0,
      },
      '& p + p': {
        marginTop: theme.spacing(.75),
      },
    },
    dsgvoError: {
      color: theme.palette.error.main,
      marginLeft: 58,
      marginTop: theme.spacing(.5),
    },
    recaptcha: dsgvoStyle(theme),
    success: {
      borderRadius: 18,
      marginTop: theme.spacing(1),
      padding: theme.spacing(2),
      backgroundColor: 'color-mix(in srgb, var(--page-builder-success, #2e7d32) 10%, #ffffff)',
    },
    actions: {
      gap: theme.spacing(1.25),
      padding: `${theme.spacing(2, 4, 3.25)} !important`,
      background: 'var(--page-builder-background, #f3ece3)',
      [theme.breakpoints.down('sm')]: {
        padding: `${theme.spacing(1.5, 2.25, 2.5)} !important`,
      },
    },
    secondaryAction: {
      borderRadius: 999,
      borderColor: alpha(modalInk, .24),
      color: 'var(--page-builder-text, #2a211a)',
      paddingInline: theme.spacing(2.5),
    },
    primaryAction: {
      borderRadius: 999,
      paddingInline: theme.spacing(3),
      background: 'linear-gradient(135deg, var(--page-builder-button-bg, #7e634b), var(--page-builder-button-hover, #4e392a))',
      color: 'var(--page-builder-button-text, #fffaf4)',
      boxShadow: `0 14px 28px ${alpha(modalShadow, .26)}`,
      '&:hover': {
        background: 'linear-gradient(135deg, var(--page-builder-button-hover, #8b6f55), var(--page-builder-button-bg, #3f2e22))',
        boxShadow: `0 18px 34px ${alpha(modalShadow, .32)}`,
      },
    },
    validationPaper: {
      borderRadius: 24,
      overflow: 'hidden',
      background: 'var(--page-builder-surface, #fffaf4)',
      boxShadow: `0 28px 70px ${alpha(modalPalette.dialogShadow, .38)}`,
      [theme.breakpoints.down('sm')]: {
        borderRadius: 20,
        margin: theme.spacing(1.5),
      },
    },
    validationTitle: {
      padding: `${theme.spacing(2.5, 3, 1.25)} !important`,
      color: 'var(--page-builder-text, #2a211a)',
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '1.55rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    validationContent: {
      display: 'grid',
      gap: theme.spacing(2),
      padding: `${theme.spacing(1, 3, 2.5)} !important`,
      background: 'linear-gradient(180deg, var(--page-builder-surface, #fffaf4) 0%, var(--page-builder-background, #f3ece3) 100%)',
      [theme.breakpoints.down('sm')]: {
        padding: `${theme.spacing(.75, 2.25, 2.25)} !important`,
      },
    },
    validationList: {
      display: 'grid',
      gap: theme.spacing(.75),
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    validationIssue: {
      borderRadius: 14,
      padding: theme.spacing(1, 1.25),
      color: theme.palette.error.dark,
      backgroundColor: alpha(theme.palette.error.main, .08),
      border: `1px solid ${alpha(theme.palette.error.main, .18)}`,
      fontSize: '.92rem',
      lineHeight: 1.45,
    },
    validationCorrectionCard: {
      display: 'grid',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      borderRadius: 18,
      backgroundColor: alpha('#fff', .74),
      border: `1px solid ${alpha(modalBorder, .12)}`,
    },
    validationDsgvo: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1),
      borderRadius: 14,
      padding: theme.spacing(1.25),
      color: 'var(--page-builder-text, #332820)',
      backgroundColor: alpha('#fff', .72),
      border: `1px solid ${alpha(modalBorder, .12)}`,
    },
    validationActions: {
      gap: theme.spacing(1),
      padding: `${theme.spacing(1.5, 3, 2.5)} !important`,
      background: 'var(--page-builder-background, #f3ece3)',
      [theme.breakpoints.down('sm')]: {
        padding: `${theme.spacing(1.25, 2.25, 2.25)} !important`,
      },
    },
  })
)

interface Props {
  open: boolean
  initialSubject?: MenuPageBuilderContactSubject
  initialMessage?: string
  onClose: () => void
}

type SubmitState = 'idle' | 'tryed' | 'loading' | 'success' | 'error'

const ContactModal: React.FC<Props> = ({
  open,
  initialSubject = 'sonstiges',
  initialMessage,
  onClose,
}) => {
  const classes = useStyles()
  const [betreff, setBetreff] = useState<MenuPageBuilderContactSubject>(initialSubject)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [telefon, setTelefon] = useState('')
  const [message, setMessage] = useState('')
  const [dsgvo, setDsgvo] = useState(false)
  const [state, setState] = useState<SubmitState>('idle')
  const [validationDialogOpen, setValidationDialogOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    setBetreff(initialSubject)
    setMessage(initialMessage || '')
    setState('idle')
    setValidationDialogOpen(false)
  }, [initialMessage, initialSubject, open])

  const telefonRequired = betreff === 'rueckruf' || betreff === 'termin'
  const emailIsValid = EmailValidator.validate(email)
  const telefonValidation = telefonRequired && telefon.trim() === ''
    ? 'Bitte geben Sie eine Telefonnummer an.'
    : isValidTelefon(telefon)
  const nameIsValid = name.trim().length >= 2
  const messageIsValid = message.trim().length > 0
  const showErrors = state === 'tryed'
  const formIsValid = nameIsValid
    && emailIsValid
    && telefonValidation === true
    && messageIsValid
    && dsgvo

  const validationIssues = useMemo(() => {
    const issues: string[] = []

    if (!nameIsValid) issues.push('Bitte geben Sie Ihren Namen an.')
    if (!emailIsValid) issues.push('Bitte geben Sie eine gültige E-Mail-Adresse an.')
    if (telefonValidation !== true) {
      issues.push(typeof telefonValidation === 'string'
        ? telefonValidation
        : 'Bitte prüfen Sie die Telefonnummer.'
      )
    }
    if (!messageIsValid) issues.push('Bitte geben Sie eine kurze Notiz an.')
    if (!dsgvo) issues.push('Bitte akzeptieren Sie die Datenschutzbestimmungen.')

    return issues
  }, [dsgvo, emailIsValid, messageIsValid, nameIsValid, telefonValidation])

  const subjectLabel = useMemo(
    () => subjectOptions.find(option => option.value === betreff)?.label || 'Kontaktanfrage',
    [betreff]
  )
  const telephoneHref = shopInfos.telephone
    ? `tel:${String(shopInfos.telephone).replace(/[^\d+]/g, '')}`
    : ''

  const sendContactRequest = async () => {
    setState('loading')

    try {
      const recaptchaToken = await getRecaptchaToken('contact_modal')
      const res = await post({
        url: 'kontakt',
        body: {
          betreff,
          name: name.trim(),
          email: email.trim(),
          telefon: telefon.trim(),
          message: message.trim(),
          shop: config.shop.shop,
          recaptchaToken,
        },
      })

      setState(res ? 'success' : 'error')
    } catch (error) {
      console.error('[ContactModal] Anfrage konnte nicht gesendet werden', error)
      setState('error')
    }
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()

    if (!formIsValid) {
      setState('tryed')
      setValidationDialogOpen(true)
      return
    }

    await sendContactRequest()
  }

  const submitValidationDialog = async () => {
    if (!formIsValid) {
      setState('tryed')
      return
    }

    setValidationDialogOpen(false)
    await sendContactRequest()
  }

  return <>
    <Dialog
      open={open}
      className={modalPalette.variables ? classes.palette : undefined}
      style={modalPalette.variables}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      PaperProps={{ className: classes.paper }}
    >
      <DialogTitle className={classes.titleBar}>
        <Typography component='span' className={classes.eyebrow}>
          Persönliche Beratung
        </Typography>
        <Typography component='div' className={classes.title}>
          {subjectLabel}
        </Typography>
        <Typography component='p' className={classes.titleSubline}>
          Hinterlassen Sie Ihre Kontaktdaten.
        </Typography>
        <IconButton
          aria-label='Kontaktmodal schließen'
          className={classes.closeButton}
          onClick={onClose}
          size='large'
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <div className={classes.intro}>
          <Typography variant='body2'>
            Ihre Anfrage landet direkt bei unserem Team. Für Rückfragen nutzen wir die unten eingetragenen Daten.
          </Typography>
          <div className={classes.directContact}>
            {shopInfos.telephone &&
              <a className={classes.contactChip} href={telephoneHref}>
                <LocalPhoneOutlinedIcon fontSize='small' />
                {shopInfos.telephone}
              </a>
            }
            {shopInfos.email &&
              <a className={classes.contactChip} href={`mailto:${shopInfos.email}`}>
                <EmailOutlinedIcon fontSize='small' />
                {shopInfos.email}
              </a>
            }
          </div>
        </div>

        {state === 'success'
          ? <Alert severity='success' className={classes.success}>
              Vielen Dank. Ihre Anfrage wurde versendet.
            </Alert>
          : <form id='contact-modal-form' className={classes.form} onSubmit={submit}>
              {state === 'error' &&
                <Alert severity='error'>
                  Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.
                </Alert>
              }

              <TextField
                label='Grund'
                select
                className={classes.field}
                value={betreff}
                onChange={event => setBetreff(event.target.value as MenuPageBuilderContactSubject)}
                InputProps={{
                  startAdornment: <InputAdornment position='start'><EventAvailableOutlinedIcon fontSize='small' /></InputAdornment>,
                }}
                fullWidth
              >
                {subjectOptions.map(option =>
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                )}
              </TextField>

              <div className={classes.formCard}>
                <div className={classes.grid}>
                  <TextField
                    label='Name'
                    className={classes.field}
                    value={name}
                    onChange={event => setName(event.target.value)}
                    error={showErrors && !nameIsValid}
                    helperText={showErrors && !nameIsValid ? 'Bitte geben Sie Ihren Namen an.' : undefined}
                    autoComplete='name'
                    InputProps={{
                      startAdornment: <InputAdornment position='start'><PersonOutlineIcon fontSize='small' /></InputAdornment>,
                    }}
                    required
                    fullWidth
                  />

                  <TextField
                    label='E-Mail-Adresse'
                    type='email'
                    className={classes.field}
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    error={showErrors && !emailIsValid}
                    helperText={showErrors && !emailIsValid ? 'Bitte geben Sie eine gültige E-Mail-Adresse an.' : undefined}
                    autoComplete='email'
                    InputProps={{
                      startAdornment: <InputAdornment position='start'><EmailOutlinedIcon fontSize='small' /></InputAdornment>,
                    }}
                    required
                    fullWidth
                  />
                </div>

                <TextField
                  label='Telefonnummer'
                  type='tel'
                  className={classes.field}
                  value={telefon}
                  onChange={event => setTelefon(event.target.value)}
                  error={showErrors && telefonValidation !== true}
                  helperText={showErrors && telefonValidation !== true ? telefonValidation : undefined}
                  autoComplete='tel'
                  InputProps={{
                    startAdornment: <InputAdornment position='start'><LocalPhoneOutlinedIcon fontSize='small' /></InputAdornment>,
                  }}
                  required={telefonRequired}
                  fullWidth
                />

                <TextField
                  label='Notiz'
                  className={classes.field}
                  value={message}
                  onChange={event => setMessage(event.target.value)}
                  error={showErrors && !messageIsValid}
                  helperText={showErrors && !messageIsValid ? 'Bitte geben Sie eine kurze Notiz an.' : undefined}
                  minRows={5}
                  multiline
                  InputProps={{
                    startAdornment: <InputAdornment position='start'><NotesOutlinedIcon fontSize='small' /></InputAdornment>,
                  }}
                  required
                  fullWidth
                />

                <div>
                  <div className={classes.dsgvo}>
                    <Switch
                      checked={dsgvo}
                      onChange={() => setDsgvo(current => !current)}
                      inputProps={{ 'aria-label': 'Datenschutz akzeptieren' }}
                    />
                    <div className={classes.dsgvoText}>
                      <p><strong>Datenschutzerklärung</strong></p>
                      <p>
                        Ich stimme zu, dass meine Angaben zur Beantwortung meiner Anfrage
                        erhoben und verarbeitet werden. Sie können Ihre Einwilligung jederzeit
                        für die Zukunft per E-Mail an {shopInfos.email} widerrufen.
                      </p>
                    </div>
                  </div>
                  {showErrors && !dsgvo &&
                    <p className={classes.dsgvoError}>Bitte akzeptieren Sie die Datenschutzbestimmungen.</p>
                  }
                </div>

                <div className={classes.recaptcha}>
                  <GoogleRecaptchaInfos />
                </div>
              </div>
            </form>
        }
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={onClose} variant='outlined' className={classes.secondaryAction} sx={pageBuilderOutlinedButtonSx}>
          {state === 'success' ? 'Schließen' : 'Abbrechen'}
        </Button>
        {state !== 'success' &&
          <Button
            type='submit'
            form='contact-modal-form'
            variant='contained'
            className={classes.primaryAction}
            sx={pageBuilderButtonSx}
            disabled={state === 'loading'}
          >
            {state === 'loading' ? 'Sendet...' : 'Absenden'}
          </Button>
        }
      </DialogActions>
    </Dialog>

    <Dialog
      open={validationDialogOpen}
      className={modalPalette.variables ? classes.palette : undefined}
      style={modalPalette.variables}
      onClose={() => setValidationDialogOpen(false)}
      fullWidth
      maxWidth='xs'
      PaperProps={{ className: classes.validationPaper }}
    >
      <DialogTitle className={classes.validationTitle}>
        Angaben kurz prüfen
      </DialogTitle>
      <DialogContent className={classes.validationContent}>
        <Alert severity='warning'>
          Ein paar Angaben fehlen noch. Sie können sie direkt hier korrigieren und danach absenden.
        </Alert>

        {validationIssues.length > 0 &&
          <ul className={classes.validationList}>
            {validationIssues.map(issue =>
              <li className={classes.validationIssue} key={issue}>{issue}</li>
            )}
          </ul>
        }

        <div className={classes.validationCorrectionCard}>
          {!nameIsValid &&
            <TextField
              label='Name'
              className={classes.field}
              value={name}
              onChange={event => setName(event.target.value)}
              autoFocus
              autoComplete='name'
              fullWidth
            />
          }

          {!emailIsValid &&
            <TextField
              label='E-Mail-Adresse'
              type='email'
              className={classes.field}
              value={email}
              onChange={event => setEmail(event.target.value)}
              autoFocus={nameIsValid}
              autoComplete='email'
              fullWidth
            />
          }

          {telefonValidation !== true &&
            <TextField
              label='Telefonnummer'
              type='tel'
              className={classes.field}
              value={telefon}
              onChange={event => setTelefon(event.target.value)}
              helperText={typeof telefonValidation === 'string' ? telefonValidation : undefined}
              autoFocus={nameIsValid && emailIsValid}
              autoComplete='tel'
              fullWidth
            />
          }

          {!messageIsValid &&
            <TextField
              label='Notiz'
              className={classes.field}
              value={message}
              onChange={event => setMessage(event.target.value)}
              autoFocus={nameIsValid && emailIsValid && telefonValidation === true}
              minRows={4}
              multiline
              fullWidth
            />
          }

          {!dsgvo &&
            <div className={classes.validationDsgvo}>
              <Switch
                checked={dsgvo}
                onChange={() => setDsgvo(current => !current)}
                inputProps={{ 'aria-label': 'Datenschutz akzeptieren' }}
              />
              <Typography variant='body2'>
                Ich akzeptiere die Datenschutzbestimmungen.
              </Typography>
            </div>
          }
        </div>
      </DialogContent>
      <DialogActions className={classes.validationActions}>
        <Button
          onClick={() => setValidationDialogOpen(false)}
          variant='outlined'
          className={classes.secondaryAction}
          sx={pageBuilderOutlinedButtonSx}
        >
          Zurück
        </Button>
        <Button
          onClick={submitValidationDialog}
          variant='contained'
          className={classes.primaryAction}
          sx={pageBuilderButtonSx}
          disabled={state === 'loading'}
        >
          {state === 'loading' ? 'Sendet...' : 'Jetzt absenden'}
        </Button>
      </DialogActions>
    </Dialog>
  </>
}

export default ContactModal
